/**
 * V2-dark post-parse project create/match dialog. Faithful logic port of
 * web/src/components/completeness/PostParseProjectDialog.tsx (the five
 * mutually-exclusive states), re-skinned to V2 tokens/primitives. The caller
 * must NOT render this when the selected project already IS the exact match.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { EstimateHeaderMetadata, Project, ProjectMatchResponse } from '../../types';
import { getProjectDisplayName } from '../../lib/utils';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Banner } from './ui/banner';
import { IconCheck, IconPlus, IconSearch, IconWarn } from './icons';
import CreateProjectForm, { type CreateProjectInitialValues } from './CreateProjectForm';
import ProjectMatchPicker from './ProjectMatchPicker';

/** Props for the V2 PostParseProjectDialog (mirrors the V1 contract). */
export interface PostParseProjectDialogProps {
  /** The header_metadata payload from the parse response. */
  header: EstimateHeaderMetadata;
  /** Match result from POST /user/projects/match. */
  match: ProjectMatchResponse | null;
  /** Currently-selected project (from ?project=<id>). */
  currentProject: Project | null;
  /** Called when the user picks an existing project. */
  onUseProject: (_project: Project) => void;
  /** Called when a new project is created via the embedded form. */
  onProjectCreated: (_project: Project) => void;
  /** Dismiss without picking anything (caller keeps parse output). */
  onClose: () => void;
}

/** Build create-form initial values from extracted header metadata. */
function buildInitialValues(header: EstimateHeaderMetadata): CreateProjectInitialValues {
  return {
    street: header.address?.street ?? '',
    city: header.address?.city ?? '',
    state: header.address?.state ?? '',
    zip: header.address?.zip ?? '',
    owner_name: header.owner_name ?? '',
    claim_number: header.claim_number ?? '',
    policy_number: header.policy_number ?? '',
    loss_type: header.loss_type ?? '',
    claim_type: header.claim_type ?? '',
  };
}

type DialogState =
  | 'selected-no-match'
  | 'selected-conflict'
  | 'no-project-exact'
  | 'no-project-fuzzy'
  | 'no-project-no-match';

/** V2-dark project create/match confirmation dialog. */
export default function PostParseProjectDialog({
  header,
  match,
  currentProject,
  onUseProject,
  onProjectCreated,
  onClose,
}: PostParseProjectDialogProps) {
  const [showCreate, setShowCreate] = useState(false);

  const exactMatch = match?.exact_match ?? null;
  const fuzzyMatches = match?.fuzzy_matches ?? [];

  const headerSummary = useMemo(() => {
    const parts: string[] = [];
    if (header.owner_name) parts.push(header.owner_name);
    if (header.address?.street) parts.push(header.address.street);
    if (header.claim_number) parts.push(`Claim ${header.claim_number}`);
    return parts.join(' · ');
  }, [header]);

  // Five mutually-exclusive states (identical classification to V1).
  const state: DialogState = useMemo(() => {
    if (currentProject) {
      if (exactMatch && exactMatch.id !== currentProject.id) return 'selected-conflict';
      return 'selected-no-match';
    }
    if (exactMatch) return 'no-project-exact';
    if (fuzzyMatches.length > 0) return 'no-project-fuzzy';
    return 'no-project-no-match';
  }, [currentProject, exactMatch, fuzzyMatches.length]);

  const initialValues = useMemo(() => buildInitialValues(header), [header]);

  if (showCreate) {
    return (
      <CreateProjectForm
        initialValues={initialValues}
        onClose={() => setShowCreate(false)}
        onCreated={onProjectCreated}
      />
    );
  }

  let title = 'Where should this estimate go?';
  let body: ReactNode = null;
  let footer: ReactNode = null;

  if (state === 'selected-no-match') {
    title = `Import into ${getProjectDisplayName(currentProject!)}?`;
    body = (
      <p className="text-[12.5px] text-[var(--tx-mid)]">
        No existing project matches this estimate. Import it into the selected project or create a
        new one from the estimate header.
      </p>
    );
    footer = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setShowCreate(true)}>
          <IconPlus /> Create new project
        </Button>
        <Button variant="primary" size="sm" onClick={() => onUseProject(currentProject!)}>
          <IconCheck /> Import here
        </Button>
      </>
    );
  } else if (state === 'selected-conflict') {
    title = 'Estimate matches a different project';
    body = (
      <Banner tone="warn" icon={<IconWarn />}>
        This estimate&apos;s claim number matches{' '}
        <strong>{getProjectDisplayName(exactMatch!)}</strong>, but your selected project is{' '}
        <strong>{getProjectDisplayName(currentProject!)}</strong>.
      </Banner>
    );
    footer = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setShowCreate(true)}>
          <IconPlus /> Create new
        </Button>
        <Button variant="secondary" size="sm" onClick={() => onUseProject(currentProject!)}>
          Stay in {getProjectDisplayName(currentProject!)}
        </Button>
        <Button variant="primary" size="sm" onClick={() => onUseProject(exactMatch!)}>
          Switch to {getProjectDisplayName(exactMatch!)}
        </Button>
      </>
    );
  } else if (state === 'no-project-exact') {
    title = `Use ${getProjectDisplayName(exactMatch!)}?`;
    // DRY: shared selection surface (exact "Use {name}" + create-new).
    body = (
      <ProjectMatchPicker
        match={{ exact_match: exactMatch, fuzzy_matches: fuzzyMatches }}
        onSelect={onUseProject}
        onCreateNew={() => setShowCreate(true)}
      />
    );
  } else if (state === 'no-project-fuzzy') {
    title = 'Possible existing projects';
    // DRY: shared fuzzy list + create-new.
    body = (
      <ProjectMatchPicker
        match={{ exact_match: exactMatch, fuzzy_matches: fuzzyMatches }}
        onSelect={onUseProject}
        onCreateNew={() => setShowCreate(true)}
      />
    );
  } else {
    // no-project-no-match
    title = 'Create project from this estimate?';
    // DRY: shared "create new" surface; keep the "match to existing" escape hatch.
    body = (
      <ProjectMatchPicker
        match={{ exact_match: null, fuzzy_matches: [] }}
        onSelect={onUseProject}
        onCreateNew={() => setShowCreate(true)}
      />
    );
    footer = (
      <Button variant="secondary" size="sm" onClick={onClose}>
        <IconSearch /> Match to existing
      </Button>
    );
  }

  return (
    <Modal
      label="Project import options"
      title={title}
      subtitle={headerSummary ? `From estimate: ${headerSummary}` : undefined}
      onClose={onClose}
      testId="post-parse-dialog"
      state={state}
      footer={footer}
    >
      {body}
    </Modal>
  );
}
